import * as React from 'react';
import { withStyles, Theme, WithStyles, createStyles } from '@material-ui/core';

import { withIntl, IntlProps } from 'ha/i18n';
import units from 'ha/theme/units';
import Grid from 'ha/ui/next/Grid';
import { Typography } from 'ha/ui/next/Typography';
import Container from 'ha/ui/next/Container';
import Img from 'ha/ui/next/Img';
import LazyComponent from 'ha/components/LazyComponent';
import { ChevronRightIcon } from 'ha/ui/next/Icons';
import Button from 'ha/ui/next/Button';
import Hidden from 'ha/ui/next/Hidden';

import { tenantVps, landlordVps } from '../constants';

export const styles = (theme: Theme) =>
  createStyles({
    container: {
      [theme.breakpoints.up('md')]: {
        flexWrap: 'nowrap',
      },
    },
    item: {
      padding: units.yx(0, 0.75),
      marginBottom: units.rem(2.5),

      '&:last-child': {
        marginBottom: 0,
      },

      [theme.breakpoints.up('md')]: {
        position: 'relative',
        marginBottom: 0,
      },
    },
    iconWrapper: {
      [theme.breakpoints.up('md')]: {
        justifyContent: 'start',
      },
    },
    icon: {
      marginBottom: units.rem(1.25),
      height: units.rem(8.125),
      maxHeight: units.rem(8.125),

      [theme.breakpoints.up('md')]: {
        marginLeft: units.rem(2),
      },
    },
    paragraphTitle: {
      fontSize: units.rem(1.125),
    },
    index: {
      fontSize: units.rem(1.125),
      fontWeight: 'bold',
      lineHeight: 1.24,
      color: '#00c5ba',
      marginRight: units.rem(0.25),

      [theme.breakpoints.up('md')]: {
        fontSize: units.rem(2.375),
        position: 'absolute',
        left: 0,
        marginRight: 0,
        lineHeight: 1,
      },
    },
    title: {
      marginBottom: units.rem(1),
      [theme.breakpoints.up('md')]: {
        justifyContent: 'start',
        marginLeft: units.rem(2),
      },
    },
    textWrapper: {
      [theme.breakpoints.up('md')]: {
        justifyContent: 'start',
      },
    },
    text: {
      [theme.breakpoints.up('md')]: {
        textAlign: 'left',
        marginLeft: units.rem(2),
      },
    },
    ctaContainer: {
      marginTop: units.rem(5),
    },
    button: {
      borderColor: '#8e5082',
    },
  });

interface Props {
  isTenant?: boolean;
}

export const HowItWorks: React.SFC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({ classes, isTenant = false, intl: { T, urlResolver } }) => {
  const vps = isTenant ? tenantVps : landlordVps;

  return (
    <div>
      <Container maxWidth="lg">
        <Grid container justify="space-between" className={classes.container}>
          {vps.map((vp, index) => (
            <Grid
              container
              item
              key={vp.title}
              className={classes.item}
              direction="column"
            >
              <Grid
                item
                container
                justify="center"
                className={classes.iconWrapper}
              >
                <LazyComponent
                  bottomOffset="-200px"
                  scrollableAncestor="window"
                >
                  <Img
                    role="presentation"
                    className={classes.icon}
                    src={vp.icon}
                    data-test-locator={`Home HowItWorks Icon ${index}`}
                  />
                </LazyComponent>
              </Grid>
              <Grid
                item
                container
                justify="center"
                className={classes.textWrapper}
              >
                <Grid
                  item
                  container
                  justify="center"
                  alignItems="baseline"
                  className={classes.title}
                >
                  <Typography.Paragraph className={classes.index}>
                    {index + 1}.
                  </Typography.Paragraph>
                  <Typography.ParagraphTitle
                    className={classes.paragraphTitle}
                    component="h3"
                    data-test-locator={`Home HowItWorks Title ${index}`}
                  >
                    {T(vp.title)}
                  </Typography.ParagraphTitle>
                </Grid>
                <Grid item>
                  <Typography.Paragraph
                    align="center"
                    className={classes.text}
                    data-test-locator={`Home HowItWorks Text ${index}`}
                  >
                    {T(vp.text)}
                  </Typography.Paragraph>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Hidden smDown>
          <Grid container justify="center" className={classes.ctaContainer}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              href={
                isTenant
                  ? urlResolver.getRenting()
                  : urlResolver.getRentingOut()
              }
            >
              <ChevronRightIcon />
              {T('Learn more')}
            </Button>
          </Grid>
        </Hidden>
      </Container>
    </div>
  );
};

export default withStyles(styles)(withIntl(HowItWorks));
